import { URLConstructor } from '../../../../../NonReact/utils/URLBuilder'
import { JSONResponse } from '../../../../../NonReact/common/Types/Types'

const POLLING_INTERVAL: number = 2000

/**
 * Reads the OAuth status endpoint, resolving null for any non-OK response.
 *
 * Deliberately a bare fetch rather than `requestGetJSON` (APD-13489). That client routes failures
 * through `RequestCache` into `redirectIfSessionExpired`, which treats ANY 401 as an expired session
 * and navigates the whole page to the login route — which then forwards a still-valid session on to
 * the landing page. This poll runs while the user is mid-consent in a popup, and the endpoint answers
 * 401 in some scopes for a session id that has no record yet, so the effect was that clicking
 * "Grant Admin Consent" threw the admin off the Telephony Server dialog a couple of seconds later,
 * losing the dialog and any unsaved edits, with consent impossible to complete because the listener
 * died with the page.
 *
 * A failed poll here means "not ready yet", never "your session is gone": every failure is already
 * swallowed so the next poll can retry. Going through the shared client only to discard its result
 * bought nothing and let a background probe navigate the page.
 *
 * Same-origin, so the session cookie is sent by default.
 *
 * @param statusLocation - URL of the status endpoint.
 * @return The parsed body, or null if the request failed or returned a non-OK status.
 */
const readStatus = async <T extends unknown>(statusLocation: URLConstructor): Promise<JSONResponse<T>> => {
    const response: Response = await fetch(statusLocation.constructURL(), {
        headers: { Accept: 'application/json' }
    })

    if (!response.ok) {
        return null as JSONResponse<T>
    }

    return (await response.json()) as JSONResponse<T>
}

/**
 * Function to open a popup window.
 *
 * @param location - URL for the popup window to go to.
 * @param name - Name of the window to open.
 * @param sizing - Sizing of the window to open.
 */
const openPopup = (location: URLConstructor, name: string, sizing: string): Nullable<Window> =>
    open(location.constructURL(), name, sizing)

/**
 * Execute the OAuth flow.
 *
 * @param location - URL for the OAuth popup window to go to.
 * @param name - Name of the window to open.
 * @param sizing - Sizing of the window to open.
 * @param statusLocation - URL for the status check.
 * @param statusSuccessFunc - Function to determine if the status check was successful.
 * @param errorOnMaxRetries - Error message to reject with if the flow does not complete in time.
 */
// Completion is detected by POLLING the status endpoint from the moment the popup opens, until the
// server records success (statusSuccessFunc), the popup goes away, or the timeout elapses. Polling is
// the reliable signal because BroadcastChannel is partitioned per top-level site: a message posted by
// the redirect JSP (top-level on the CCS site) is dropped when the listener runs inside the cross-site
// New-UI iframe. It is therefore kept only as an optional fast-path trigger, never the sole signal.
//
// The same poll also watches `popup.closed`, which is what stops the caller's spinner when the user
// dismisses the Microsoft window instead of completing consent. Without it the flow sat pending until
// the 5-minute timeout, so the button span for five minutes and then reported a failure the user had
// already caused deliberately.
//
// `popup.closed` IS readable here. COOP severs the popup's access to its opener (window.opener,
// cross-window scripting, postMessage) but does not stop the opener reading WindowProxy.closed —
// verified against login.microsoftonline.com, where it stays false while the window is open and flips
// to true once it is dismissed.
//
// BroadcastChannel signals (fast path only):
//   'oauth-complete' — RedirectSuccess.jsp loaded → check status immediately rather than wait for the next poll.
//   'oauth-failed'   — RedirectFailed.jsp loaded → fail immediately.
const OAUTH_FLOW_TIMEOUT_MS: number = 5 * 60 * 1000

export const executeOAuthFlow = <T extends unknown>(
    location: URLConstructor,
    name: string,
    sizing: string,
    statusLocation: URLConstructor,
    statusSuccessFunc: (response: JSONResponse<T>) => boolean,
    errorOnMaxRetries: string
): Promise<Nullable<T>> => {
    const popup = openPopup(location, name, sizing)

    if (!popup) {
        return Promise.reject(new Error(errorOnMaxRetries))
    }

    return new Promise<Nullable<T>>((resolve, reject): void => {
        let settled = false

        const cleanup = (): void => {
            broadcastChannel.close()
            clearInterval(pollID)
            clearTimeout(timeoutID)
        }

        const succeed = (value: Nullable<T>): void => {
            if (settled) return

            settled = true
            cleanup()
            resolve(value)
        }

        const fail = (): void => {
            if (settled) return

            settled = true
            cleanup()
            reject(new Error(errorOnMaxRetries))
        }

        // A single status check. Resolves the flow if the server now reports success; when
        // `failIfNotSuccessful` is set (the popup has gone) an unsuccessful result ends the flow
        // instead of waiting for the timeout. Transient errors are swallowed so a blip does not abort
        // the flow — unless this is the final check, where an error cannot be retried.
        const checkStatus = (failIfNotSuccessful: boolean = false): void => {
            readStatus<T>(statusLocation)
                .then((status: JSONResponse<T>): void => {
                    if (statusSuccessFunc(status)) {
                        succeed(status)
                        return
                    }

                    if (failIfNotSuccessful) {
                        fail()
                    }
                })
                .catch((): void => {
                    if (failIfNotSuccessful) {
                        fail()
                    }
                })
        }

        // Reliable signal: poll from the outset, independent of BroadcastChannel delivery. The poll
        // also notices a dismissed popup.
        //
        // A closed popup does NOT by itself mean the user gave up: on success RedirectSuccess.jsp
        // closes itself, so the two cases are indistinguishable from the window alone. One last status
        // check decides which it was, so a completed consent is never reported as a cancellation just
        // because its window closed first.
        const poll = (): void => {
            if (popup.closed) {
                clearInterval(pollID)
                checkStatus(true)
                return
            }

            checkStatus()
        }

        const pollID: ReturnType<typeof setInterval> = setInterval(poll, POLLING_INTERVAL)

        // Optional fast path: react to the redirect JSP's BroadcastChannel message when it is
        // deliverable (e.g. standalone / same-site), but never depend on it.
        const broadcastChannel = new BroadcastChannel('akixi-oauth-complete')

        broadcastChannel.onmessage = (event: MessageEvent): void => {
            if (event.data === 'oauth-complete') {
                checkStatus()
            } else if (event.data === 'oauth-failed') {
                fail()
            }
        }

        const timeoutID: ReturnType<typeof setTimeout> = setTimeout(fail, OAUTH_FLOW_TIMEOUT_MS)
    })
}
